import React, { useState } from 'react';
import './MappingGrid.css';
import { ColumnMappingDetail } from '../../types/columnMappingReview';
import MappingDetails from './MappingDetails';

interface MappingGridProps {
  mappings: ColumnMappingDetail[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onSort: (sortBy: 'last_used' | 'usage_count' | 'created_at', order: 'asc' | 'desc') => void;
  onEdit: (mappingId: string, newMetadataName: string) => void;
  onDelete: (mappingId: string) => void;
}

const MappingGrid: React.FC<MappingGridProps> = ({
  mappings,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  onSort,
  onEdit,
  onDelete
}) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [sortColumn, setSortColumn] = useState<'last_used' | 'usage_count' | 'created_at'>('last_used');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Domain color mapping for visual distinction
  const getDomainColor = (domain: string) => {
    const colors: { [key: string]: string } = {
      treasury: '#3b82f6',     // Blue
      ap: '#ef4444',           // Red
      ar: '#10b981',           // Green
      gl: '#8b5cf6',           // Purple
      revenue: '#f59e0b',      // Amber
      general: '#6b7280',      // Gray
    };
    return colors[domain] || '#6b7280';
  };

  const handleSort = (column: 'last_used' | 'usage_count' | 'created_at') => {
    const newOrder = sortColumn === column && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortColumn(column);
    setSortOrder(newOrder);
    onSort(column, newOrder);
  };

  const handleEdit = (mapping: ColumnMappingDetail) => {
    setEditingRow(mapping.id);
    setEditValue(mapping.metadata_name);
    setExpandedRow(null);
  };

  const handleSaveEdit = async (mappingId: string) => {
    if (editValue.trim()) {
      await onEdit(mappingId, editValue.trim());
      setEditingRow(null);
      setEditValue('');
    }
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditValue('');
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const getStalenessIndicator = (lastUsed: string | null) => {
    if (!lastUsed) return 'never-used';
    const date = new Date(lastUsed);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) return 'active';
    if (diffDays < 90) return 'moderate';
    return 'stale';
  };

  if (loading) {
    return <div className="loading">Loading mappings...</div>;
  }

  return (
    <div className="mapping-grid-container">
      <table className="mapping-grid">
        <thead>
          <tr>
            <th className="column-expand"></th>
            <th>Original Column</th>
            <th>Mapped To</th>
            <th>Semantic Type</th>
            <th>Domain</th>
            <th 
              className="sortable"
              onClick={() => handleSort('usage_count')}
            >
              Usage Count {sortColumn === 'usage_count' && (sortOrder === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              className="sortable"
              onClick={() => handleSort('last_used')}
            >
              Last Used {sortColumn === 'last_used' && (sortOrder === 'asc' ? '↑' : '↓')}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {mappings.map((mapping) => (
            <React.Fragment key={mapping.id}>
              <tr className={`mapping-row ${getStalenessIndicator(mapping.last_used)}`}>
                <td className="column-expand">
                  <button
                    className="expand-button"
                    onClick={() => setExpandedRow(expandedRow === mapping.id ? null : mapping.id)}
                  >
                    {expandedRow === mapping.id ? '−' : '+'}
                  </button>
                </td>
                <td className="original-column">{mapping.original_column}</td>
                <td className="metadata-name">
                  {editingRow === mapping.id ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit(mapping.id);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      autoFocus
                    />
                  ) : (
                    mapping.metadata_name
                  )}
                </td>
                <td className="semantic-type">
                  {mapping.semantic_type ? (
                    <span 
                      className="semantic-badge"
                      title={mapping.business_context || 'No context available'}
                      style={{
                        backgroundColor: '#f3f4f6',
                        color: '#4b5563',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: mapping.business_context ? 'help' : 'default'
                      }}
                    >
                      {mapping.semantic_type.replace(/_/g, ' ')}
                    </span>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '11px' }}>-</span>
                  )}
                </td>
                <td>
                  {mapping.domain ? (
                    <span 
                      className="domain-badge"
                      style={{
                        backgroundColor: `${getDomainColor(mapping.domain)}20`,
                        color: getDomainColor(mapping.domain),
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        border: `1px solid ${getDomainColor(mapping.domain)}40`
                      }}
                    >
                      {mapping.domain}
                    </span>
                  ) : (
                    <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>-</span>
                  )}
                </td>
                <td className="usage-count">
                  {mapping.usage_count >= 99 ? '99+' : mapping.usage_count}
                  {mapping.usage_count === 0 && (
                    <span className="unused-indicator">⚠️</span>
                  )}
                </td>
                <td className={`last-used ${getStalenessIndicator(mapping.last_used)}`}>
                  {formatDate(mapping.last_used)}
                </td>
                <td className="actions">
                  {editingRow === mapping.id ? (
                    <>
                      <button 
                        className="action-btn save"
                        onClick={() => handleSaveEdit(mapping.id)}
                        title="Save"
                      >
                        ✓
                      </button>
                      <button 
                        className="action-btn cancel"
                        onClick={handleCancelEdit}
                        title="Cancel"
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        className="action-btn edit"
                        onClick={() => handleEdit(mapping)}
                        title="Edit"
                      >
                        ✎
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => onDelete(mapping.id)}
                        title="Delete"
                      >
                        🗑
                      </button>
                    </>
                  )}
                </td>
              </tr>
              {expandedRow === mapping.id && (
                <tr className="expanded-row">
                  <td colSpan={7}>
                    <MappingDetails mapping={mapping} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {mappings.length === 0 && (
        <div className="no-mappings">
          No mappings found. Try adjusting your filters.
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default MappingGrid;